import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, CheckCircle2, History, LayoutDashboard, ListChecks, Lock, Plug, Send, Settings } from "lucide-react";
import StartScreen from "./StartScreen";
import { BasicsStep, ConnectionStep } from "./ConnectionSteps";
import { CallsStep, HistoryPanel, PublishStep, useLifecycle } from "./EndpointSteps";
import { TryStep } from "./TryPanel";
import { BUILDER_VIEW, useMachine } from "./machines";
import { OverviewPanel, SettingsPanel } from "./ManageView";
import { ErrorNote, IntegrationLogo, StatusBadge, Switch, statusOf } from "./parts";
import { useProviderLogo } from "../../hooks/useProviderLogo";
import {
  createInHouse,
  defineApi,
  getInHouse,
  updateInHouse
} from "../../services/inHouseService";
import { canAuthorInHouse } from "../../services/authService";
import OrbitLoader from "../ui/OrbitLoader";

/**
 * In-house integration builder.
 * Routes: /integrations/in-house/new and /integrations/in-house/:integrationId
 *
 * A draft opens on the setup steps: Start (docs link / by hand) → Basics → Connection →
 * Calls → Try it → Go live. A published one (live or disabled) opens on its manage view
 * instead: Overview, the same panels as tabs, History and Settings. The server's status
 * picks the view, so publishing or unpublishing moves between them on the next refresh.
 * "Developer view" reveals the technical layer without changing either.
 */

const HOME = "/dashboard?tab=in-house";

const STEPS = [
  { id: "basics", label: "Basics", hint: "Name & description" },
  { id: "connection", label: "Connection", hint: "Address & sign-in" },
  { id: "calls", label: "Calls", hint: "What Orbit can do" },
  { id: "test", label: "Try it", hint: "Send a real request" },
  { id: "publish", label: "Go live", hint: "Make it available" }
];

const MANAGE_TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "calls", label: "Calls", icon: ListChecks },
  { id: "connection", label: "Connection", icon: Plug },
  { id: "test", label: "Try it", icon: Send },
  { id: "history", label: "History", icon: History },
  { id: "settings", label: "Settings", icon: Settings }
];

const DEV_MODE_KEY = "orbit.inHouse.devMode";

/** Developer view is a per-person preference, so it survives reloads; storage may be blocked. */
const readDevMode = () => {
  try {
    return localStorage.getItem(DEV_MODE_KEY) === "1";
  } catch {
    return false;
  }
};

const writeDevMode = (on) => {
  try {
    localStorage.setItem(DEV_MODE_KEY, on ? "1" : "0");
  } catch {
    // Not remembered; the toggle still works for this visit.
  }
};

/** Sign-in is set up; a disabled integration has its connection off on purpose. */
const signInSaved = (integration) =>
  !!integration.connection?.connected ||
  (integration.status === "disabled" && !!integration.connection?.secret_configured);

/** A step is ticked only when the server state says it is done. */
const stepDone = (id, integration, tested) => {
  if (!integration) return false;
  if (id === "basics") return true;
  if (id === "connection") return signInSaved(integration);
  if (id === "calls") return integration.endpoints.length > 0;
  if (id === "test") return tested.size > 0;
  return integration.status === "live";
};

/** Where to land when opening an existing integration. */
const firstStep = (integration) => {
  if (integration.status !== "draft") return "publish";
  if (!integration.api || !signInSaved(integration)) return "connection";
  return "calls";
};

const nameFrom = (preview, source) => {
  if (preview.title) return preview.title.trim().slice(0, 110);
  try {
    return new URL(source.url).hostname;
  } catch {
    return "New integration";
  }
};

/**
 * Importing the same docs twice is common while trying things out; rather than
 * failing on the taken name, use the next free "Name (2)".
 */
async function createWithFreeName(base) {
  let last;
  for (let n = 1; n <= 20; n += 1) {
    last = await createInHouse({ name: n === 1 ? base : `${base} (${n})` });
    if (last.success || last.status !== 409) return last;
  }
  return last;
}

/**
 * /new and /:integrationId render the same component in the same place, so React would
 * keep one instance across the move from "new" to the created integration, and every
 * useState initialiser (the step, the pending import, the address the file proposed)
 * would be skipped. A key per address gives each its own fresh builder.
 */
export default function InHouseBuilder() {
  const { integrationId } = useParams();
  return <Builder key={integrationId || "new"} />;
}

function Builder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { integrationId } = useParams();

  const [integration, setIntegration] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [step, setStep] = useState(location.state?.step || null);
  const [view, sendView] = useMachine("builder view", BUILDER_VIEW, integrationId ? "loading" : "start");
  const [pendingImport, setPendingImport] = useState(location.state?.pendingImport || null);
  const [tested, setTested] = useState(() => new Set());
  const [devMode, setDevMode] = useState(readDevMode);
  const [tab, setTab] = useState("overview");
  const [logoVersion, setLogoVersion] = useState(0);
  const logoUrl = useProviderLogo(integration?.id, logoVersion);
  const logo = { url: logoUrl, reload: () => setLogoVersion((v) => v + 1) };

  const refresh = useCallback(async () => {
    if (!integrationId) return null;
    const result = await getInHouse(integrationId);
    if (result.success) {
      setIntegration(result.data);
      setLoadError("");
    } else setLoadError(result.message);
    return result.data;
  }, [integrationId]);
  const lifecycle = useLifecycle(integration, refresh);

  // Every load replays the server's status into the view, so setup vs manage always matches it.
  useEffect(() => {
    if (integration) sendView(statusOf(integration) === "Draft" ? "syncDraft" : "syncPublished");
  }, [integration, sendView]);

  useEffect(() => {
    if (!integrationId) return;
    refresh().then((data) => {
      if (data) setStep((s) => s || firstStep(data));
    });
  }, [integrationId, refresh]);

  /**
   * Created only after the spec was read, so a failed read leaves nothing behind.
   * Resolves to { success, message } so the start screen can show a failure.
   */
  const startFromImport = async (source, preview) => {
    const created = await createWithFreeName(nameFrom(preview, source));
    if (!created.success) return created;
    let apiNote = null;
    if (preview.base_url) {
      // Anything the server refuses (e.g. an address it can't reach) is fixed on
      // the Connection step, which is prefilled from the file either way.
      const defined = await defineApi(created.data.id, {
        base_url: preview.base_url,
        auth: preview.auth || { kind: "api_key", location: "header", name: "X-API-Key" }
      });
      if (!defined.success) apiNote = defined.message;
    }
    navigate(`/integrations/in-house/${created.data.id}`, {
      replace: true,
      state: { step: "connection", pendingImport: { source, preview }, apiNote }
    });
    return { success: true };
  };

  /** Import done or dismissed: forget it, including in history, so a reload doesn't reopen it. */
  const clearPendingImport = () => {
    setPendingImport(null);
    navigate(location.pathname, { replace: true, state: { step } });
  };

  /** Basics creates the integration on first save; after that it only edits. */
  const saveBasics = async (body) => {
    if (integration) {
      const result = await updateInHouse(integration.id, body);
      if (result.success) setIntegration(result.data);
      return result;
    }
    const result = await createInHouse(body);
    if (result.success) {
      navigate(`/integrations/in-house/${result.data.id}`, { replace: true, state: { step: "connection" } });
    }
    return result;
  };

  if (!canAuthorInHouse()) {
    return <AdminsOnly onBack={() => navigate(HOME)} />;
  }

  if (view === "start") {
    return (
      <div className="flex-1 overflow-auto bg-[#FCFCFC]">
        <BackLink onBack={() => navigate(HOME)} />
        <StartScreen
          onImported={startFromImport}
          onScratch={() => {
            setStep("basics");
            sendView("scratch");
          }}
        />
      </div>
    );
  }

  if (view === "loading") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-[#FCFCFC]">
        {loadError ? (
          <>
            <ErrorNote message={loadError} />
            <button type="button" onClick={() => navigate(HOME)} className="mt-4 rounded-lg px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50">
              Back to In-House Integrations
            </button>
          </>
        ) : (
          <OrbitLoader size={40} label="Loading integration" />
        )}
      </div>
    );
  }

  if (view === "celebrate") {
    return <PublishedScreen integration={integration} onDone={() => navigate(HOME)} onStay={() => sendView("keepEditing")} />;
  }

  const status = integration ? statusOf(integration) : "Draft";
  const managing = view === "manage";
  const current = managing ? tab : step || "basics";
  const index = STEPS.findIndex((s) => s.id === current);
  const go = (offset) => setStep(STEPS[index + offset].id);
  const props = { integration, refresh, devMode, logo };
  // Tabs have no Back/Continue; the setup steps walk forward.
  const walk = managing ? {} : { onBack: () => go(-1), onNext: () => go(1) };
  // What the imported file proposed, for when it couldn't be saved as the API yet.
  const proposedApi = pendingImport?.preview && !integration?.api
    ? { base_url: pendingImport.preview.base_url, auth: pendingImport.preview.auth, note: location.state?.apiNote }
    : null;

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-[#FCFCFC]">
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-8 py-4">
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => navigate(HOME)} className="rounded-lg p-2 hover:bg-gray-100" aria-label="Back to In-House Integrations">
            <ArrowLeft className="h-5 w-5 text-gray-700" />
          </button>
          <IntegrationLogo name={integration?.name} logoUrl={logoUrl} className="h-10 w-10 text-base" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900">{integration?.name || "New integration"}</h1>
              <StatusBadge status={status} />
            </div>
            <p className="text-xs text-gray-500">In-House Integrations · changes save as you go</p>
          </div>
        </div>
        <Switch
          checked={devMode}
          onChange={(on) => {
            setDevMode(on);
            writeDevMode(on);
          }}
          label="Developer view"
        />
      </header>

      <div className="flex flex-1 overflow-hidden">
        {managing ? (
          <ManageNav current={current} onOpen={setTab} />
        ) : (
          <nav className="w-64 shrink-0 border-r border-gray-200 bg-white px-4 py-6" aria-label="Builder steps">
            <ol className="space-y-1">
              {STEPS.map((s, i) => {
                const active = s.id === current;
                const done = !active && stepDone(s.id, integration, tested);
                // Nothing past Basics exists until the integration has been created.
                const locked = !integration && s.id !== "basics";
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      disabled={locked}
                      aria-current={active ? "step" : undefined}
                      onClick={() => setStep(s.id)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${active ? "bg-blue-50" : "hover:bg-gray-50"}`}
                    >
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${active ? "bg-blue-600 text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                        {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                      </span>
                      <span>
                        <span className={`block text-sm ${active ? "font-semibold text-gray-900" : "font-medium text-gray-700"}`}>{s.label}</span>
                        <span className="block text-xs text-gray-500">{s.hint}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {integration && (
              <button
                type="button"
                onClick={() => setStep("history")}
                className={`mt-6 flex w-full items-center gap-3 rounded-lg border-t border-gray-100 px-3 pt-4 pb-2 text-left text-sm font-medium ${current === "history" ? "text-blue-700" : "text-gray-600 hover:text-gray-900"}`}
              >
                <History className="h-4 w-4" /> History
              </button>
        )}
        </nav>
        )}

        <main className="flex-1 overflow-auto">
          <div className={`mx-auto px-8 py-8 ${current === "test" ? "max-w-6xl" : current === "calls" ? "max-w-5xl" : "max-w-3xl"}`}>
            {/* A later reload can fail too; say so instead of leaving stale details unexplained. */}
            {loadError && (
              <div className="mb-4">
                <ErrorNote message={`Couldn't reload the latest details: ${loadError}`} />
              </div>
            )}
            {current === "overview" && <OverviewPanel integration={integration} lifecycle={lifecycle} onOpen={setTab} />}
            {current === "basics" && (
              <BasicsStep key={integration?.id || "new"} integration={integration} logo={logo} onSave={saveBasics} onNext={() => integration && setStep("connection")} />
            )}
            {current === "connection" && <ConnectionStep {...props} {...walk} proposed={proposedApi} />}
            {current === "calls" && (
              <CallsStep
                {...props}
                {...walk}
                pendingImport={pendingImport}
                setPendingImport={setPendingImport}
                clearPendingImport={clearPendingImport}
              />
            )}
            {current === "test" && (
              <TryStep {...props} {...walk} tested={tested} markTested={(id) => setTested((t) => new Set(t).add(id))} />
            )}
            {current === "publish" && (
              <PublishStep
                {...props}
                tested={tested}
                onBack={() => go(-1)}
                onOpen={setStep}
                onPublished={() => sendView("published")}
                onDecommissioned={() => navigate(HOME, { replace: true })}
              />
            )}
            {current === "settings" && (
              <SettingsPanel
                integration={integration}
                logo={logo}
                lifecycle={lifecycle}
                onSaveBasics={saveBasics}
                onDecommissioned={() => navigate(HOME, { replace: true })}
              />
            )}
            {current === "history" && <HistoryPanel integration={integration} devMode={devMode} />}
          </div>
        </main>
      </div>
    </div>
  );
}

/** Tabs for a published integration: no numbers or ticks, because nothing is left to finish. */
function ManageNav({ current, onOpen }) {
  return (
    <nav className="w-56 shrink-0 border-r border-gray-200 bg-white px-3 py-6" aria-label="Integration sections">
      <ul className="space-y-1">
        {MANAGE_TABS.map(({ id, label, icon: Icon }) => {
          const active = id === current;
          return (
            <li key={id}>
              <button
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => onOpen(id)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition ${
                  active ? "bg-blue-50 font-semibold text-blue-700" : "font-medium text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function AdminsOnly({ onBack }) {
  return (
    <div className="flex flex-1 items-center justify-center bg-[#FCFCFC] px-6">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <Lock className="h-6 w-6 text-gray-500" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900">Only admins can set up in-house integrations</h1>
        <p className="mt-2 text-sm text-gray-600">
          Ask an admin in your organization to add or change one. Published in-house integrations are
          available to you in event routing, workflows and the AI builder.
        </p>
        <button type="button" onClick={onBack} className="mt-6 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700">
          Back to In-House Integrations
        </button>
      </div>
    </div>
  );
}

function BackLink({ onBack }) {
  return (
    <div className="px-8 pt-6">
      <button type="button" onClick={onBack} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100">
        <ArrowLeft className="h-4 w-4" /> In-House Integrations
      </button>
    </div>
  );
}

function PublishedScreen({ integration, onDone, onStay }) {
  const count = integration.endpoints.length;
  return (
    <div className="flex flex-1 items-center justify-center bg-[#FCFCFC] px-6">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">{integration.name} is live</h1>
        <p className="mt-2 text-gray-600">
          {count} call{count === 1 ? " is" : "s are"} now available to your team in event routing, workflows and the AI builder.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <button type="button" onClick={onStay} className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100">
            Keep editing
          </button>
          <button type="button" onClick={onDone} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700">
            Back to In-House Integrations
          </button>
        </div>
      </div>
    </div>
  );
}
